import { push } from '@lagunovsky/redux-react-router'
import { compact, includes, isNil, template } from 'lodash'
import PropTypes from 'prop-types'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import { nbPerPageLabel } from '../../../../../referencial/constants/ReferencialConstants'
import { NewTable } from 'components/datatable/NewTable'
import useListIndexed from 'utils/customHook/useListIndexed'
import useSandreList from 'utils/customHook/useSandreList'
import useListGrouped from 'utils/customHook/useListGrouped'
import { Button, Card, CardContent, Dialog, DialogActions, DialogContent, Grid, Icon, Tooltip } from '@mui/material'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import Select from 'components/forms/Select'
import Textarea from 'components/forms/Textarea'
import useBoolean from 'utils/customHook/useBoolean'
import { QualityCampaignContext } from 'quality/components/campaign/QualityCampaign'
import ConfirmModal from 'components/modal/ConfirmModal'
import { negIdGenerator } from 'campaign/utils/CampaignUtils'
import { getTimestamp } from 'utils/DateUtil'
import { SHOW_INSEE_CODE, SHOW_SANDRE_CODE } from 'account/constants/AccountSettingsConstants'
import useAccountSetting from 'utils/customHook/useAccountSetting'
import useApplicationSetting from 'utils/customHook/useApplicationSetting'

const headers = [{ key: 'localisationError', value: '' }, 'code', 'city', 'name', 'codification', 'samplePoint']

const hasLocalisationError = (x, y, projection) => {
    const count = isNil(x) + isNil(y) + isNil(projection) // add boolean give number
    return count === 1 || count === 2
}

const UpdatePopin = ({
    isOpen = false,
    onClose = () => { },
    onValidate = () => { },

    campaignStation = {},
}) => {
    const {
        qualitometers,
        allPoints,
    } = useSelector(store => ({
        qualitometers: store.QualityReducer.qualitometersLight,
        allPoints: store.QualityReducer.allPoints,
    }), shallowEqual)
    const { campaign } = useContext(QualityCampaignContext)
    const indexedQualitometers = useListIndexed(qualitometers, 'id')
    const samplePointsList = useMemo(() => allPoints.filter(p => (
        p.idQualitometer === campaignStation.stationId
        && (isNil(p.endDate) || p.endDate >= getTimestamp(campaign.beginningApplication) || p.endDate <= getTimestamp(campaign.endingApplication))
        && (isNil(p.startDate) || p.startDate <= getTimestamp(campaign.beginningApplication) || p.startDate >= getTimestamp(campaign.endingApplication))
    ),
    ), [allPoints, campaignStation.stationId, campaign])
    const codificationsList = useSandreList('QUALITOMETRES.NOMENCLATURES')

    const generalShowSandreCode = useApplicationSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : true)
    const showSandreCode = useAccountSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : generalShowSandreCode)

    const [codification, setCodification] = useState()
    const [samplePoint, setSamplePoint] = useState()
    const [comment, setComment] = useState()

    useEffect(() => {
        if (!isOpen) return

        setCodification(campaignStation.codification)
        setSamplePoint(campaignStation.samplePoint)
        setComment(campaignStation.comment)
    }, [isOpen])

    return (
        <Dialog
            onClose={onClose}
            fullWidth
            maxWidth='md'
            open={isOpen}
            slotProps={{
                paper: {
                    sx: {
                        minHeight: undefined,
                        maxHeight: undefined,
                    },
                },
            }}
        >
            <DefaultDialogTitle
                title={indexedQualitometers[campaignStation.stationId]?.displayLabel ?? ''}
                onClose={onClose}
            />
            <DialogContent>
                <Card>
                    <CardContent>
                        <Grid container spacing={1}>
                            <Grid size={6}>
                                <Select
                                    label={i18n.codification}
                                    value={codification}
                                    options={codificationsList}
                                    onChange={setCodification}
                                    keyValue='code'
                                    keyLabel='name'
                                />
                            </Grid>
                            <Grid size={6}>
                                <Select
                                    label={i18n.samplePoint}
                                    value={samplePoint}
                                    options={samplePointsList}
                                    onChange={setSamplePoint}
                                    keyValue='idPoint'
                                    keyLabel={showSandreCode ? 'displayLabel' : 'name'}
                                    sortFunc={({ idPoint }) => idPoint}
                                />
                            </Grid>
                            <Grid size={12}>
                                <Textarea
                                    title={i18n.comment}
                                    value={comment}
                                    onChange={setComment}
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onValidate({ codification, samplePoint, comment })} variant='contained' color='primary'>
                    {i18n.validate}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

UpdatePopin.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    onValidate: PropTypes.func,
    campaignStation: PropTypes.shape({
        codification: PropTypes.number,
        samplePoint: PropTypes.number,
        comment: PropTypes.string,
    }),
}

const TableQualityCampaignStations = () => {
    const dispatch = useDispatch()
    const {
        isEditMode,
        campaignStations, setCampaignStations,
        campaignPlannings, setCampaignPlannings,
        campaignPlanningsExceptions, setCampaignPlanningsExceptions,
    } = useContext(QualityCampaignContext)
    const {
        qualitometers,
        cities,
        allPoints,
    } = useSelector(store => ({
        qualitometers: store.QualityReducer.qualitometersLight,
        cities: store.CityReducer.cities,
        allPoints: store.QualityReducer.allPoints,
    }), shallowEqual)
    const indexedQualitometers = useListIndexed(qualitometers, 'id')
    const indexedCities = useListIndexed(cities, 'id')
    const groupedSamplePoints = useListGrouped(allPoints, 'idQualitometer')
    const codificationsList = useSandreList('QUALITOMETRES.NOMENCLATURES')

    const generalShowInseeCode = useApplicationSetting(SHOW_INSEE_CODE, setting => setting ? parseInt(setting) : true)
    const showInseeCode = useAccountSetting(SHOW_INSEE_CODE, setting => setting ? parseInt(setting) : generalShowInseeCode)
    const generalShowSandreCode = useApplicationSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : true)
    const showSandreCode = useAccountSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : generalShowSandreCode)

    const {
        value: isConfirmOpen,
        setTrue: onOpenConfirm,
        setFalse: onCloseConfirm,
    } = useBoolean(false)
    const {
        value: isOpen,
        setTrue: onOpen,
        setFalse: onClose,
    } = useBoolean(false)
    const [selectedStationIndex, setSelectedStationIndex] = useState()

    const countPlanning = (stationId) => {
        return campaignPlannings.reduce((nb, planning) => {
            if (planning.stationsPlan.includes(stationId)
                || campaignPlanningsExceptions.find(({ planningId, stationsPlan }) => (
                    planningId === planning.id && includes(stationsPlan, stationId)
                ))
            ) return nb + 1
            return nb
        }, 0)
    }

    const formattedStations = campaignStations.map(cs => {
        const qualito = indexedQualitometers[cs.stationId] ?? {}
        const samplePoint = groupedSamplePoints[cs.stationId]?.find(p => p.idPoint === cs.samplePoint)
        return {
            id: qualito.id,
            localisationError: hasLocalisationError(qualito.x, qualito.y, qualito.projection) ? <Tooltip title={i18n.localisationIncomplete}><Icon size='small'>warning</Icon></Tooltip> : '',
            code: {
                value: qualito.code,
                onClick: () => dispatch(push(`/station/quality/${qualito.id}`)),
            },
            city: qualito.townCode ? `${indexedCities[qualito.townCode]?.name} ${showInseeCode ? `[${qualito.townCode}]` : ''}` : null,
            name: qualito.name,
            codification: codificationsList.find(c => c.code === cs.codification)?.name,
            samplePoint: showSandreCode ? samplePoint?.displayLabel : samplePoint?.name,
            nbUsedInPlanning: countPlanning(cs.id),
        }
    })

    const nbUsedInPlanning = formattedStations[selectedStationIndex]?.nbUsedInPlanning ?? 0

    const onUpdateStation = (index, changes) => setCampaignStations(prevCS => prevCS.toSpliced(index, 1, { ...prevCS[index], ...changes }))
    const onDuplicateStation = (index) => setCampaignStations(prevCS =>[...prevCS, { ...prevCS[index], id: negIdGenerator.next().value }])
    const onDeleteStation = (index) => {
        const idToDelete = campaignStations[index]?.id
        setCampaignPlannings(prevCP => prevCP.map(planning => ({ ...planning, stationsPlan: planning.stationsPlan.filter(id => id !== idToDelete) })))
        setCampaignStations(prevCS => prevCS.toSpliced(index, 1))
        setCampaignPlanningsExceptions(prevCPE => compact(prevCPE.map(exception => {
            const newException = {
                ...exception,
                stationsPlan: exception.stationsPlan.filter(id => id !== idToDelete),
            }
            if (newException.stationsPlan.length === 0) return undefined
            return newException
        })))
    }

    return (
        <>
            <NewTable
                rows={formattedStations}
                headers={headers}
                lineActions={[{
                    icon: 'close',
                    onClick: (row, index) => {
                        if (row.nbUsedInPlanning === 0) onDeleteStation(index)
                        else {
                            setSelectedStationIndex(index)
                            onOpenConfirm()
                        }
                    },
                    displayed: isEditMode,
                }, {
                    icon: 'add_to_photos', // duplicate
                    onClick: (_, index) => onDuplicateStation(index),
                    displayed: isEditMode,
                }, {
                    icon: 'edit',
                    onClick: (_, index) => {
                        setSelectedStationIndex(index)
                        onOpen()
                    },
                    displayed: isEditMode,
                }]}
                rowsPerPageOptions={nbPerPageLabel}
            />
            <UpdatePopin
                isOpen={isOpen}
                onClose={onClose}
                onValidate={(changes) => {
                    onUpdateStation(selectedStationIndex, changes)
                    onClose()
                }}

                campaignStation={campaignStations[selectedStationIndex]}
            />
            <ConfirmModal
                isOpen={isConfirmOpen}
                title={i18n.deletingConfirmation}
                subTitle={nbUsedInPlanning > 1 ? template(i18n.stationUsedInplannings)({ nb: nbUsedInPlanning }) : i18n.stationUsedInplanning}
                onValidate={() => {
                    onDeleteStation(selectedStationIndex)
                    onCloseConfirm()
                }}
                onClose={onCloseConfirm}
            />
        </>
    )
}

TableQualityCampaignStations.propTypes = {}

export default TableQualityCampaignStations